import { c as _c } from "react/compiler-runtime";
import type { TurnkeyWallet } from "@/app/(v2)/_types/wallet";
import { useOrganizationWallets } from "@/hooks/useOrganizationWallets";
import { B3OS_ORG_COOKIE_NAME } from "@/lib/authenticatedFetch";
import Cookies from "js-cookie";
import { useCallback, useEffect, useState } from "react";
interface UseTurnkeyWalletsOptions {
  /**
   * Whether to automatically select the first wallet when loaded
   */
  autoSelectFirst?: boolean;
  /**
   * Pre-selected wallet address
   */
  preSelectedAddress?: string;
  /**
   * Callback when a wallet is selected (for auto-selection or external changes)
   */
  onWalletSelected?: (wallet: TurnkeyWallet) => void;
  /**
   * Only load wallets when this is true (useful for modals)
   */
  enabled?: boolean;
  /**
   * Organization ID to load wallets from (if not provided, uses cookie)
   */
  organizationId?: string;
}
interface UseTurnkeyWalletsReturn {
  wallets: TurnkeyWallet[];
  isLoading: boolean;
  error: Error | null;
  selectedWallet: TurnkeyWallet | null;
  setSelectedWallet: (address: string) => void;
  refetch: () => Promise<void>;
}

/**
 * Hook to manage Turnkey wallets
 *
 * NEW ARCHITECTURE (organization-centric):
 * - Fetches wallets from b3os-workflow API
 * - Wallets are stored at organization level, not user level
 * - Uses organization ID from cookie or prop
 *
 * OLD ARCHITECTURE (user-centric via B3 API) is no longer used
 */
export function useTurnkeyWallets(t0) {
  const $ = _c(32);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    autoSelectFirst: t2,
    preSelectedAddress,
    onWalletSelected,
    enabled: t3,
    organizationId: propOrgId
  } = t1;
  const autoSelectFirst = t2 === undefined ? false : t2;
  const enabled = t3 === undefined ? true : t3;
  const [selectedWalletAddress, setSelectedWalletAddress] = useState(preSelectedAddress);
  let t4;
  if ($[2] !== propOrgId) {
    t4 = propOrgId || Cookies.get(B3OS_ORG_COOKIE_NAME);
    $[2] = propOrgId;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const organizationId = t4;
  const {
    wallets: orgWallets,
    isLoading,
    error: orgError,
    refetch
  } = useOrganizationWallets(enabled ? organizationId : undefined);
  let t5;
  if ($[4] !== orgWallets) {
    t5 = orgWallets.map(_temp);
    $[4] = orgWallets;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const wallets = t5;
  let t6;
  if ($[6] !== orgError) {
    t6 = orgError ? new Error(orgError.message || "Failed to load wallets") : null;
    $[6] = orgError;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const error = t6;
  let t7;
  let t8;
  if ($[8] !== autoSelectFirst || $[9] !== enabled || $[10] !== isLoading || $[11] !== onWalletSelected || $[12] !== preSelectedAddress || $[13] !== selectedWalletAddress || $[14] !== wallets) {
    t7 = () => {
      if (!enabled || isLoading || wallets.length === 0) {
        return;
      }
      if (preSelectedAddress && wallets.some(w_1 => w_1.address === preSelectedAddress)) {
        setSelectedWalletAddress(preSelectedAddress);
        const wallet = wallets.find(w_0 => w_0.address === preSelectedAddress);
        if (wallet && onWalletSelected) {
          onWalletSelected(wallet);
        }
      } else {
        if (autoSelectFirst && !selectedWalletAddress) {
          const firstWallet = wallets[0];
          setSelectedWalletAddress(firstWallet.address);
          if (onWalletSelected) {
            onWalletSelected(firstWallet);
          }
        }
      }
    };
    t8 = [wallets, enabled, isLoading, preSelectedAddress, autoSelectFirst, selectedWalletAddress, onWalletSelected];
    $[8] = autoSelectFirst;
    $[9] = enabled;
    $[10] = isLoading;
    $[11] = onWalletSelected;
    $[12] = preSelectedAddress;
    $[13] = selectedWalletAddress;
    $[14] = wallets;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t9;
  if ($[17] !== onWalletSelected || $[18] !== wallets) {
    t9 = address => {
      setSelectedWalletAddress(address);
      const wallet_0 = wallets.find(w_2 => w_2.address === address);
      if (wallet_0 && onWalletSelected) {
        onWalletSelected(wallet_0);
      }
    };
    $[17] = onWalletSelected;
    $[18] = wallets;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const handleSetSelectedWallet = t9;
  let t10;
  if ($[20] !== selectedWalletAddress || $[21] !== wallets) {
    t10 = wallets.find(w_3 => w_3.address === selectedWalletAddress) || null;
    $[20] = selectedWalletAddress;
    $[21] = wallets;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const selectedWallet = t10;
  let t11;
  if ($[23] !== refetch) {
    t11 = async () => {
      await refetch();
    };
    $[23] = refetch;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== error || $[26] !== handleSetSelectedWallet || $[27] !== isLoading || $[28] !== selectedWallet || $[29] !== t11 || $[30] !== wallets) {
    t12 = {
      wallets,
      isLoading,
      error,
      selectedWallet,
      setSelectedWallet: handleSetSelectedWallet,
      refetch: t11
    };
    $[25] = error;
    $[26] = handleSetSelectedWallet;
    $[27] = isLoading;
    $[28] = selectedWallet;
    $[29] = t11;
    $[30] = wallets;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
}
function _temp(w) {
  return {
    address: w.address,
    walletId: w.providerWalletId,
    walletName: w.name,
    subOrgId: undefined
  };
}